import {
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthStatus, useAuth } from '../auth/Auth';
import LoadingPage from '../loading/LoadingPage';
import Avatar from './info/Avatar';
import CreatedAtChip from './info/CreatedAtChip';
import TitleAndDescription from './info/TitleAndDescription';
import DisplayName from './info/DisplayName';
import { useApi } from '../api/Api';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SlideUpTransition from '../components/SlideUpTransition';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { RequestSnackbar, useRequest } from '../api/Request';
import { useEffect, useState } from 'react';
import { PaymentModel } from '../theSource/constants/PaymentModel';
import { ResponseType } from '../theSource/constants/ResponseType';
import Icon from '../components/Icon';
import NotificationSettingsEditor from './editor/NotificationSettingsEditor';
import { Person, QuestionAnswer } from '@mui/icons-material';
import ArtistSubscriptionSettingsEditor from './editor/ArtistSubscriptionSettingEditor';
import SubscriptionManager from './editor/SubscriptionManager';
import NotFoundPage from '../NotFoundPage';
import { User } from '../database/user';

const ProfilePage = () => {
    const auth = useAuth();
    const api = useApi();
    const { username } = useParams<{ username: string }>();
    const navigate = useNavigate();
    const getUserPublicRequest = useRequest<User>();
    const deleteUserRequest = useRequest();
    const theme = useTheme();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [publicUser, setPublicUser] = useState<User>();
    const [loading, setLoading] = useState(!!username);

    // Determine if viewing own profile or public profile
    const isOwnProfile = !username;
    const user = isOwnProfile ? auth.user! : publicUser;

    useEffect(() => {
        if (username && !getUserPublicRequest.isSent()) {
            getUserPublicRequest.onStart();
            api.getUserPublic(username)
                .then((resp) => {
                    setPublicUser(resp.data);
                    getUserPublicRequest.onSuccess();
                })
                .catch((err) => {
                    console.error(err);
                    getUserPublicRequest.onFailure(err);
                })
                .finally(() => setLoading(false));
        }
    }, [username, api, getUserPublicRequest]);

    if (auth.status === AuthStatus.Loading || loading) {
        return <LoadingPage />;
    }

    if (!user) {
        return <NotFoundPage />;
    }

    const handleDelete = () => {
        deleteUserRequest.onStart();
        api.deleteUser()
            .then(() => {
                deleteUserRequest.onSuccess('Successfully deleted post');
                auth.deleteUser();
                navigate('/', { replace: true });
            })
            .catch((err) => {
                console.error('Delete community post: ', err);
                deleteUserRequest.onFailure(err);
            });
    };

    return (
        <Container maxWidth='md' sx={{ pt: 6, mb: '100px' }}>
            {/* Only show delete dialog for own profile */}
            {isOwnProfile && (
                <Dialog
                    open={showDeleteDialog}
                    onClose={
                        deleteUserRequest.isLoading()
                            ? undefined
                            : () => setShowDeleteDialog(false)
                    }
                    TransitionComponent={SlideUpTransition}
                    PaperProps={{
                        sx: {
                            borderColor: 'rgba(211, 47, 47, 0.4)',
                            boxShadow: '0 24px 64px rgba(0,0,0,0.85), 0 0 0 1px rgba(211,47,47,0.2)',
                        },
                    }}
                >
                    <RequestSnackbar request={deleteUserRequest} />
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningAmberIcon sx={{ color: 'error.main', fontSize: '1.3rem' }} />
                        Delete your account?
                        <IconButton
                            aria-label='close'
                            onClick={() => setShowDeleteDialog(false)}
                            sx={{ position: 'absolute', right: 10, top: 8 }}
                            disabled={deleteUserRequest.isLoading()}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText
                            sx={{
                                color: `${theme.palette.secondary.main}`,
                            }}
                        >
                            Are you sure you want to delete this user? You can't undo this
                            action.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color='secondary'
                            onClick={() => setShowDeleteDialog(false)}
                            disabled={deleteUserRequest.isLoading()}
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            color='error'
                            variant='contained'
                            onClick={() => handleDelete()}
                            loading={deleteUserRequest.isLoading()}
                        >
                            Delete Account
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            )}
            <Stack spacing={4}>
                <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    flexWrap='wrap'
                    rowGap={2}
                >
                    <Stack
                        direction='row'
                        alignItems='center'
                        spacing={2}
                        flexWrap='wrap'
                        rowGap={1}
                    >
                        <Avatar user={user} size={150} />
                        <Stack spacing={2} alignItems='center'>
                            <DisplayName displayName={user.displayName} />

                            <CreatedAtChip createdAt={user.createdAt} />
                        </Stack>
                    </Stack>
                    {/* Only show edit/delete buttons for own profile */}
                    {isOwnProfile && (
                        <Stack direction='row' spacing={2}>
                            <Button
                                id='edit-profile-button'
                                variant='contained'
                                onClick={() => navigate('/profile/edit')}
                            >
                                Edit Profile
                            </Button>
                            <Button
                                id='delete-profile-Button'
                                variant='contained'
                                onClick={() => setShowDeleteDialog(true)}
                            >
                                Delete Profile
                            </Button>
                        </Stack>
                    )}
                </Stack>
                <Typography variant='h5' gutterBottom>
                    <Person
                        style={{
                            verticalAlign: 'middle',
                            marginRight: '0.1em',
                        }}
                    />
                    Personal Info
                </Typography>
                <TitleAndDescription title='Bio' description={user.bio} />
                <TitleAndDescription
                    title='Quote'
                    description={user.quote ? `"${user.quote}"` : undefined}
                />
                {/* Only show private sections for own profile */}
                {isOwnProfile && user?.isArtist && user.artistInfo && (
                    <>
                        <TitleAndDescription
                            title='Fun Fact'
                            description={user.artistInfo.funFact}
                        />
                        <TitleAndDescription
                            title='Track ID'
                            description={user.artistInfo.musicId}
                        />
                        <TitleAndDescription
                            title='YouTube Video ID'
                            description={user.artistInfo.videoId}
                        />
                        <Stack>
                            <Typography variant='h5' gutterBottom>
                                <QuestionAnswer
                                    style={{
                                        verticalAlign: 'middle',
                                        marginRight: '0.1em',
                                    }}
                                />{' '}
                                Requests Payment Model
                            </Typography>
                            <TitleAndDescription
                                description={`Selected Payment Model: ${user.artistInfo.paymentModel}`}
                            />
                            {user.artistInfo.paymentModel === PaymentModel.STANDARD && (
                                <Stack
                                    direction='row'
                                    alignItems='center'
                                    spacing={2}
                                    pl={4}
                                >
                                    <Stack spacing={2} alignItems='center'>
                                        <Typography
                                            variant='body1'
                                            sx={{ fontFamily: 'LemonMilk' }}
                                        >
                                            <Icon
                                                name={ResponseType.Text}
                                                color='primary'
                                                sx={{
                                                    marginRight: '0.4rem',
                                                    verticalAlign: 'middle',
                                                }}
                                            />
                                            Text
                                        </Typography>
                                        <Divider
                                            sx={{
                                                height: '4px',
                                                backgroundColor:
                                                    theme.palette.primary.main,
                                                width: '100%',
                                            }}
                                        />
                                        <Typography>
                                            ${user.artistInfo.costTextResponseType}
                                        </Typography>
                                    </Stack>

                                    <Stack spacing={2} alignItems='center'>
                                        <Typography
                                            variant='body1'
                                            sx={{ fontFamily: 'LemonMilk' }}
                                        >
                                            <Icon
                                                name={ResponseType.Video}
                                                color='primary'
                                                sx={{
                                                    marginRight: '0.4rem',
                                                    verticalAlign: 'middle',
                                                }}
                                            />
                                            Video
                                        </Typography>
                                        <Divider
                                            sx={{
                                                height: '4px',
                                                backgroundColor:
                                                    theme.palette.primary.main,
                                                width: '100%',
                                            }}
                                        />
                                        <Typography>
                                            ${user.artistInfo.costVideoResponseType}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            )}
                            {user.artistInfo.paymentModel === PaymentModel.PWYW && (
                                <Stack
                                    direction={{ md: 'column', lg: 'row' }}
                                    spacing={4}
                                >
                                    <Stack spacing={2} alignItems='center'>
                                        <Typography
                                            variant='h6'
                                            sx={{ fontFamily: 'LemonMilk' }}
                                        >
                                            <Icon
                                                name={ResponseType.Text}
                                                color='primary'
                                                sx={{
                                                    marginRight: '0.4rem',
                                                    verticalAlign: 'middle',
                                                }}
                                            />
                                            Text
                                        </Typography>
                                        <Divider
                                            sx={{
                                                height: '4px',
                                                backgroundColor:
                                                    theme.palette.primary.main,
                                                width: '100%',
                                            }}
                                        />
                                        <Stack
                                            spacing={2}
                                            direction='row'
                                            alignItems='center'
                                        >
                                            <Typography>{`Min: $${user.artistInfo.pwywPrice?.text.min}`}</Typography>
                                            <Typography>{`Suggested: $${user.artistInfo.pwywPrice?.text.suggested}`}</Typography>
                                            <Typography>{`Max: $${user.artistInfo.pwywPrice?.text.max}`}</Typography>
                                        </Stack>
                                    </Stack>

                                    <Stack spacing={2} alignItems='center'>
                                        <Typography
                                            variant='h6'
                                            sx={{ fontFamily: 'LemonMilk' }}
                                        >
                                            <Icon
                                                name={ResponseType.Video}
                                                color='primary'
                                                sx={{
                                                    marginRight: '0.4rem',
                                                    verticalAlign: 'middle',
                                                }}
                                            />
                                            Video
                                        </Typography>
                                        <Divider
                                            sx={{
                                                height: '4px',
                                                backgroundColor:
                                                    theme.palette.primary.main,
                                                width: '100%',
                                            }}
                                        />
                                        <Stack
                                            spacing={2}
                                            direction='row'
                                            alignItems='center'
                                        >
                                            <Typography>{`Min: $${user.artistInfo.pwywPrice?.video.min}`}</Typography>
                                            <Typography>{`Suggested: $${user.artistInfo.pwywPrice?.video.suggested}`}</Typography>
                                            <Typography>{`Max: $${user.artistInfo.pwywPrice?.video.max}`}</Typography>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    </>
                )}
                {isOwnProfile && (
                    <>
                        <ArtistSubscriptionSettingsEditor
                            user={user}
                            subscriptionSettings={user.artistInfo?.subscriptionSettings}
                            disabled={true}
                        />
                        <NotificationSettingsEditor
                            user={user}
                            notificationSettings={user.userNotificationSettings}
                            disabled={true}
                        />
                        <SubscriptionManager user={user} />
                        <Button
                            onClick={() => auth.signout()}
                            startIcon={<ExitToAppIcon color='error' />}
                            sx={{
                                alignSelf: 'start',
                                minWidth: 'auto',
                            }}
                            variant='outlined'
                            color='error'
                        >
                            Sign Out
                        </Button>
                    </>
                )}
            </Stack>
        </Container>
    );
};

export default ProfilePage;
